import { makeAutoObservable } from "mobx";
import { ReactNode } from "react";
import { onError } from "src/common/onError";

import { getUniqueKey } from "../../getUniqueKey";
import { loadClsBranch } from "../apiClasses";
import { ZClsNode } from "./ZClsNode";

type Node = ZClsNode & {
  key: string;
  items?: Node[];
};

type VisualNode = {
  key: string;
  title: ReactNode;
  children?: VisualNode[];
  isLeaf: boolean;
};

const makeBranch = (src: ZClsNode[]): Node[] =>
  src.map((raw) => ({ ...raw, key: getUniqueKey(raw.id) }));

export class ClassesTreeStore {
  constructor(root: ZClsNode[]) {
    this.root = makeBranch(root);
    makeAutoObservable(this);
  }

  root: Node[];

  updateRoot() {
    this.root = [...this.root];
  }

  activeNodeKey: string | null = null;

  setActiveNodeKey(key: string | null) {
    this.activeNodeKey = key;
  }

  get selectedKeys(): string[] {
    const { activeNodeKey } = this;
    return activeNodeKey ? [activeNodeKey] : [];
  }

  get activeNode(): Node | undefined {
    const { activeNodeKey } = this;
    if (!activeNodeKey) return undefined;
    return this.findRootNodeByKey(activeNodeKey);
  }

  get visualTree(): VisualNode[] {
    const makeVisualNodeTitle = ({ name, childrenCount }: Node): string => {
      let res = name;
      if (childrenCount) {
        res += ` (${childrenCount})`;
      }
      return res;
    };
    const cvtNode = (src: Node): VisualNode => ({
      key: src.key,
      title: makeVisualNodeTitle(src),
      children: cvtBranch(src.items),
      isLeaf: !src.hasChildren,
    });
    const cvtBranch = (srcNodes?: Node[]): VisualNode[] | undefined =>
      srcNodes?.map((node) => cvtNode(node));
    return cvtBranch(this.root) ?? [];
  }

  async loadBranch(vNode: VisualNode) {
    try {
      const node = this.findRootNodeByKey(vNode.key);
      if (node && !node.items) {
        node.items = makeBranch(await loadClsBranch(node.id));
        this.updateRoot();
      }
    } catch (e) {
      onError(e);
      throw e; // чтобы компонент дерева знал, что данные не загрузились
    }
  }

  findRootNodeByKey(key: string): Node | undefined {
    const find = (branch: Node[]): Node | undefined => {
      // eslint-disable-next-line no-restricted-syntax
      for (const node of branch) {
        if (node.key === key) return node;
        if (node.items) {
          const res = find(node.items);
          if (res) return res;
        }
      }
      return undefined;
    };
    return find(this.root);
  }

  findRootNodeById(id: string): Node | undefined {
    if (!id) return undefined;
    const find = (branch: Node[]): Node | undefined => {
      // eslint-disable-next-line no-restricted-syntax
      for (const node of branch) {
        if (node.id === id) return node;
        if (node.items) {
          const res = find(node.items);
          if (res) return res;
        }
      }
      return undefined;
    };
    return find(this.root);
  }
}
